// Croatian speech recognition. Three implementations behind one API:
//
//   iOS native     → BallingoSpeech (our local Swift plugin at
//                    ios/App/App/BallingoSpeechPlugin.swift; needed because
//                    @capacitor-community/speech-recognition is CocoaPods-only
//                    and Capacitor 8 ships SPM by default).
//   Android native → @capacitor-community/speech-recognition (works on Gradle).
//   Web            → window.SpeechRecognition / webkitSpeechRecognition.
//                    iOS WKWebView does NOT expose this — onError fires
//                    'unavailable' and Speak.jsx auto-skips.
//
// API:
//   const session = await startRecognition({
//     lang: 'hr-HR',
//     onPartial: (text) => {...},
//     onFinal:   (text) => {...},
//     onError:   (err)  => {...},
//   });
//   session.stop();   // idempotent

import { Capacitor, registerPlugin } from '@capacitor/core';

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// Lazy JS proxy for the iOS Swift plugin. Safe to call on any platform —
// returns a stub object whose method calls throw, which we catch.
const BallingoSpeech = registerPlugin('BallingoSpeech');

// Android-only path: the community plugin's Gradle module.
let androidPluginPromise = null;
function getAndroidPlugin() {
  if (Capacitor.getPlatform() !== 'android') return Promise.resolve(null);
  if (!androidPluginPromise) {
    androidPluginPromise = import('@capacitor-community/speech-recognition')
      .then((mod) => mod.SpeechRecognition)
      .catch(() => null);
  }
  return androidPluginPromise;
}

// ─── iOS — BallingoSpeech native plugin ──────────────────────────────────

async function startIOS({ lang, onPartial, onFinal, onError }) {
  try {
    const avail = await BallingoSpeech.available();
    if (!avail?.available) { onError?.({ code: 'unavailable' }); return { stop() {} }; }
    let perm = await BallingoSpeech.checkPermissions();
    if (perm.speechRecognition !== 'granted' || perm.microphone !== 'granted') {
      perm = await BallingoSpeech.requestPermissions();
      if (perm.speechRecognition !== 'granted' || perm.microphone !== 'granted') {
        onError?.({ code: 'denied' });
        return { stop() {} };
      }
    }
    let partialHandle = null;
    if (onPartial) {
      partialHandle = await BallingoSpeech.addListener('partialResults', (data) => {
        const text = data?.matches?.[0];
        if (text) onPartial(text);
      });
    }
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      partialHandle?.remove?.();
      BallingoSpeech.stop().catch(() => {});
    };
    BallingoSpeech.start({ language: lang, partialResults: true })
      .then((res) => { onFinal?.(res?.matches?.[0] || ''); })
      .catch((err) => { onError?.({ code: 'runtime', error: err }); })
      .finally(() => { partialHandle?.remove?.(); });
    return { stop };
  } catch (err) {
    onError?.({ code: 'runtime', error: err });
    return { stop() {} };
  }
}

// ─── Android — @capacitor-community/speech-recognition ───────────────────

async function startAndroid({ lang, onPartial, onFinal, onError }) {
  const SR = await getAndroidPlugin();
  if (!SR) { onError?.({ code: 'unavailable' }); return { stop() {} }; }
  try {
    const avail = await SR.available();
    if (!avail?.available) { onError?.({ code: 'unavailable' }); return { stop() {} }; }
    const perm = await SR.checkPermissions();
    if (perm.speechRecognition !== 'granted') {
      const req = await SR.requestPermissions();
      if (req.speechRecognition !== 'granted') {
        onError?.({ code: 'denied' });
        return { stop() {} };
      }
    }
    let partialHandle = null;
    if (onPartial) {
      partialHandle = await SR.addListener('partialResults', (data) => {
        const text = data?.matches?.[0];
        if (text) onPartial(text);
      });
    }
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      partialHandle?.remove?.();
      SR.stop().catch(() => {});
    };
    SR.start({ language: lang, partialResults: true, popup: false, maxResults: 1 })
      .then((res) => { onFinal?.(res?.matches?.[0] || ''); })
      .catch((err) => { onError?.({ code: 'runtime', error: err }); })
      .finally(() => { partialHandle?.remove?.(); });
    return { stop };
  } catch (err) {
    onError?.({ code: 'runtime', error: err });
    return { stop() {} };
  }
}

// ─── Web — SpeechRecognition / webkitSpeechRecognition ───────────────────

function startWeb({ lang, onPartial, onFinal, onError }) {
  const Ctor =
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!Ctor) { onError?.({ code: 'unavailable' }); return { stop() {} }; }

  const rec = new Ctor();
  rec.lang = lang;
  rec.interimResults = !!onPartial;
  rec.continuous = false;
  rec.maxAlternatives = 1;

  let finalText = '';
  let stopped = false;

  rec.onresult = (e) => {
    let interim = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const r = e.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (interim && onPartial) onPartial(interim);
  };
  rec.onend = () => {
    if (stopped) return;
    stopped = true;
    onFinal?.(finalText.trim());
  };
  rec.onerror = (e) => {
    if (stopped) return;
    stopped = true;
    onError?.({ code: e.error || 'runtime', error: e });
  };

  try { rec.start(); } catch (err) {
    onError?.({ code: 'runtime', error: err });
    return { stop() {} };
  }

  return {
    stop() {
      if (stopped) return;
      try { rec.stop(); } catch {}
    },
  };
}

// ─── Dispatch ────────────────────────────────────────────────────────────

export async function startRecognition(opts) {
  if (isNative()) {
    return Capacitor.getPlatform() === 'ios' ? startIOS(opts) : startAndroid(opts);
  }
  return startWeb(opts);
}

export async function recognitionAvailable() {
  if (isNative()) {
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const a = await BallingoSpeech.available();
        return !!a?.available;
      } catch { return false; }
    }
    const SR = await getAndroidPlugin();
    if (!SR) return false;
    try {
      const a = await SR.available();
      return !!a?.available;
    } catch { return false; }
  }
  if (typeof window === 'undefined') return false;
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}
